import { useState } from "react";
import { PawPrint, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { services } from "@/data/site";

const inputCls =
  "w-full rounded-md border border-input bg-white px-3.5 py-2.5 text-sm text-charcoal placeholder:text-mutedgreen/70 transition-colors focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40";
const labelCls = "mb-1.5 block text-sm font-semibold text-forest";

const initial = {
  firstName: "", lastName: "", phone: "", email: "", serviceAddress: "", city: "", zip: "",
  serviceType: "", itemsToRemove: "", amount: "", preferredDate: "", contactMethod: "Call",
  hasDog: "No", dogRestrictions: "", notes: "", agree: false,
};

export default function EstimateForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.firstName.trim()) er.firstName = "Required";
    if (!form.lastName.trim()) er.lastName = "Required";
    if (!form.phone.trim()) er.phone = "Required";
    else if (!/[\d]{7,}/.test(form.phone.replace(/\D/g, ""))) er.phone = "Enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.itemsToRemove.trim()) er.itemsToRemove = "Tell us what needs to go";
    if (!form.agree) er.agree = "Please acknowledge to continue";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      const first = document.querySelector(`[data-error="true"]`);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatus("submitting");
    const payload = { "form-name": "estimate", ...form, agree: form.agree ? "yes" : "no" };
    try {
      await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });
    } catch (_) {
      // On non-Netlify preview environments the endpoint may not exist.
      // We still show the success state so the flow is demonstrable.
    } finally {
      setStatus("success");
      window.scrollTo({ top: document.getElementById("estimate")?.offsetTop - 90 || 0, behavior: "smooth" });
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-border bg-white p-8 text-center sm:p-12" data-testid="estimate-success">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15">
          <CheckCircle2 className="h-8 w-8 text-gold-dark" />
        </div>
        <h3 className="mt-5 font-heading text-2xl font-bold text-forest">Thanks! Request received.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-charcoal/70">
          Your estimate request has been received. A member of Benji's team will be in touch soon.
        </p>
        <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-mutedgreen">
          <PawPrint className="h-4 w-4 text-gold-dark" /> Benji says thank you!
        </p>
      </div>
    );
  }

  return (
    <form
      name="estimate"
      method="POST"
      action="/__forms.html"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={onSubmit}
      className="rounded-lg border border-border bg-white p-6 sm:p-8"
      data-testid="estimate-form"
      noValidate
    >
      <input type="hidden" name="form-name" value="estimate" />
      <p className="hidden">
        <label>Don't fill this out: <input name="bot-field" onChange={() => {}} /></label>
      </p>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First Name" required error={errors.firstName}>
          <input className={inputCls} value={form.firstName} onChange={set("firstName")} data-testid="input-firstName" autoComplete="given-name" />
        </Field>
        <Field label="Last Name" required error={errors.lastName}>
          <input className={inputCls} value={form.lastName} onChange={set("lastName")} data-testid="input-lastName" autoComplete="family-name" />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="input-phone" autoComplete="tel" />
        </Field>
        <Field label="Email Address" error={errors.email}>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="input-email" autoComplete="email" />
        </Field>
        <Field label="Service Address" className="sm:col-span-2">
          <input className={inputCls} value={form.serviceAddress} onChange={set("serviceAddress")} data-testid="input-address" autoComplete="street-address" />
        </Field>
        <Field label="City">
          <input className={inputCls} value={form.city} onChange={set("city")} data-testid="input-city" />
        </Field>
        <Field label="ZIP Code">
          <input className={inputCls} value={form.zip} onChange={set("zip")} data-testid="input-zip" inputMode="numeric" />
        </Field>
        <Field label="Type of Service">
          <select className={inputCls} value={form.serviceType} onChange={set("serviceType")} data-testid="input-serviceType">
            <option value="">Select a service…</option>
            {services.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="Other / Not sure">Other / Not sure</option>
          </select>
        </Field>
        <Field label="What needs to be removed?" required error={errors.itemsToRemove} className="sm:col-span-2">
          <textarea rows={3} className={inputCls} value={form.itemsToRemove} onChange={set("itemsToRemove")} data-testid="input-items" placeholder="e.g. old sectional couch, a fridge, and some garage boxes" />
        </Field>
        <Field label="Approximate amount of junk">
          <select className={inputCls} value={form.amount} onChange={set("amount")} data-testid="input-amount">
            <option value="">Select…</option>
            <option>Single item</option>
            <option>A few items</option>
            <option>About half a truck</option>
            <option>A full truck</option>
            <option>Not sure</option>
          </select>
        </Field>
        <Field label="Preferred Service Date">
          <input type="date" className={inputCls} value={form.preferredDate} onChange={set("preferredDate")} data-testid="input-date" />
        </Field>

        <Field label="Preferred Contact Method" className="sm:col-span-2">
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {["Call", "Text", "Email"].map((m) => (
              <label key={m} className={`cursor-pointer rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${form.contactMethod === m ? "border-gold bg-gold/15 text-forest" : "border-input text-charcoal/70 hover:border-gold/60"}`}>
                <input type="radio" name="contactMethod" value={m} checked={form.contactMethod === m} onChange={set("contactMethod")} className="sr-only" data-testid={`contact-${m.toLowerCase()}`} />
                {m}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Do you have a dog?" className="sm:col-span-2">
          <div className="flex gap-2" role="radiogroup">
            {["Yes", "No"].map((m) => (
              <label key={m} className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${form.hasDog === m ? "border-gold bg-gold/15 text-forest" : "border-input text-charcoal/70 hover:border-gold/60"}`}>
                <input type="radio" name="hasDog" value={m} checked={form.hasDog === m} onChange={set("hasDog")} className="sr-only" data-testid={`dog-${m.toLowerCase()}`} />
                {m === "Yes" && <PawPrint className="h-4 w-4 text-gold-dark" />}{m}
              </label>
            ))}
          </div>
        </Field>

        {form.hasDog === "Yes" && (
          <Field label="Any dog allergies or treat restrictions?" className="sm:col-span-2">
            <input className={inputCls} value={form.dogRestrictions} onChange={set("dogRestrictions")} data-testid="input-dogRestrictions" placeholder="Optional: let us know so we bring the right treat" />
          </Field>
        )}

        <Field label="Photos (optional)" className="sm:col-span-2">
          <div className="flex items-center gap-3 rounded-md border border-dashed border-input bg-accent/50 px-4 py-5 text-sm text-mutedgreen">
            <Upload className="h-5 w-5 shrink-0" />
            <span>Prefer to send photos? Text them to us for the fastest estimate. Photo upload here is coming soon.</span>
          </div>
        </Field>

        <Field label="Additional Notes" className="sm:col-span-2">
          <textarea rows={2} className={inputCls} value={form.notes} onChange={set("notes")} data-testid="input-notes" placeholder="Access details, timing, anything else we should know" />
        </Field>
      </div>

      <label className={`mt-6 flex items-start gap-3 text-sm ${errors.agree ? "text-destructive" : "text-charcoal/70"}`} data-error={errors.agree ? "true" : "false"}>
        <input type="checkbox" checked={form.agree} onChange={set("agree")} data-testid="input-agree" className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-gold-dark" />
        <span>I understand this request is for an estimate and does not guarantee a final price or appointment.</span>
      </label>

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="estimate-submit"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gold px-6 py-4 text-base font-bold text-charcoal shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gold-dark hover:shadow-md disabled:opacity-70 sm:w-auto"
      >
        {status === "submitting" ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : <>Request My Free Estimate <PawPrint className="h-4 w-4" strokeWidth={2.4} /></>}
      </button>
    </form>
  );
}

function Field({ label, required, error, className = "", children }) {
  return (
    <div className={className} data-error={error ? "true" : "false"}>
      <label className={labelCls}>
        {label} {required && <span className="text-gold-dark">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs font-semibold text-destructive" data-testid="field-error">{error}</p>}
    </div>
  );
}
